import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@wds/button';
import { generateMetricsAttribute } from '@sm/metrics';
import { useSearchParams } from '~app/pages/AppsDirectory/components/utils';

export const AppDetailsActionButton = ({ appDetails, stretched, target, inModal }) => {
  const urlParams = useSearchParams();

  const getEventNameFromSource = source => {
    switch (source) {
      case 'collector_list':
        return 'list';
      case 'website_collector':
        return 'weblink';
      case 'weblink_collector':
        return 'embedded';
      default:
        return '';
    }
  };

  const addCollectEventInfo = () => {
    const source = urlParams.get('ut_source');
    return source
      ? {
          eventName: `collect ${getEventNameFromSource(source)} user interaction`,
          interactionType: 'click',
          interactionLocation: 'integration card',
          integrationOptionClicked: appDetails.name,
        }
      : {};
  };

  const openLink = url => {
    window.open(url, target);
  };

  return (
    <Choose>
      <When condition={appDetails.details.linkType === 'VISIT_SITE'}>
        <Button onClick={() => openLink(appDetails.details.link)} color="primary" stretched={stretched}>
          Visit Site
        </Button>
      </When>
      <When condition={appDetails.details.linkType === 'LAUNCH'}>
        <Button onClick={() => openLink(appDetails.details.link)} color="primary" stretched={stretched}>
          Launch
        </Button>
      </When>
      <When condition={appDetails.details.linkType === 'UPGRADE'}>
        <Button
          onClick={() => openLink(appDetails.details.link)}
          color="upgrade"
          stretched={stretched}
          data-sm-metrics={
            inModal
              ? generateMetricsAttribute({
                  data: {
                    amplitudeEvent: 'experiment event',
                    eventName: 'connect upgrade clicked',
                    actionType: 'app_details.connect_upgrade_click',
                    actionFlow: 'browsing',
                    interactionType: 'click',
                    interactionLocation: 'upgrade',
                    integrationOptionClicked: appDetails.name,
                  },
                })
              : undefined
          }
        >
          Upgrade
        </Button>
      </When>
      <Otherwise>
        <Button
          onClick={() => openLink(appDetails.details.link)}
          color="primary"
          stretched={stretched}
          data-sm-metrics={
            inModal
              ? generateMetricsAttribute({
                  data: {
                    amplitudeEvent: 'experiment event',
                    eventName: 'connect install clicked',
                    actionType: 'app_details.connect_install_click',
                    actionFlow: 'browsing',
                    interactionType: 'click',
                    interactionLocation: 'install',
                    integrationOptionClicked: appDetails.name,
                  },
                })
              : generateMetricsAttribute({
                  data: {
                    actionType: 'app_details.install_click',
                    actionFlow: 'browsing',
                    ...addCollectEventInfo(appDetails),
                  },
                })
          }
        >
          Install
        </Button>
      </Otherwise>
    </Choose>
  );
};

AppDetailsActionButton.propTypes = {
  appDetails: PropTypes.shape({
    id: PropTypes.string,
    isIntegration: PropTypes.bool,
    name: PropTypes.string,
    tagline: PropTypes.string,
    fullDescription: PropTypes.string,
    logo: PropTypes.string,
    installed: PropTypes.bool,
    details: PropTypes.shape({
      publisher: PropTypes.string,
      featureList: PropTypes.arrayOf(PropTypes.string),
      googleAnalyticsTrackingId: PropTypes.string,
      supportEmail: PropTypes.string,
      supportPhoneNumber: PropTypes.string,
      supportUrl: PropTypes.string,
      requirements: PropTypes.arrayOf(PropTypes.string),
      screenshots: PropTypes.arrayOf(PropTypes.string),
      privacyPolicyUrl: PropTypes.string,
      termsOfUseUrl: PropTypes.string,
      blogUrl: PropTypes.string,
      youtubeUrl: PropTypes.string,
      websiteUrl: PropTypes.string,
      installUrl: PropTypes.string,
      upgradeRequired: PropTypes.bool,
      optionalUpgrade: PropTypes.bool,
      scopes: PropTypes.arrayOf(PropTypes.string),
      link: PropTypes.string,
      linkType: PropTypes.string,
    }),
  }).isRequired,
  stretched: PropTypes.bool.isRequired,
  target: PropTypes.string,
  inModal: PropTypes.bool,
};

AppDetailsActionButton.defaultProps = {
  target: '_self',
  inModal: false,
};

export default AppDetailsActionButton;
